import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Compass, Search, FileText, CheckCircle, Users, TrendingUp, Clock, Award, 
  Video, Building, Instagram, Twitter, Facebook, Linkedin, Shield, Target, 
  Globe, Sparkles, GraduationCap, Heart, ArrowRight, Play, Star,
  BookOpen, Zap, BarChart3, PenTool, Mic, Eye, ChevronRight, Layout, Database, MessageSquare,
  DollarSign, X
} from 'lucide-react';
import { Badge } from '../components/ui/badge';

const Landing = () => {
  // Student images for the page
  const studentImages = {
    hero: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&q=80",
    studying: "https://images.unsplash.com/photo-1758270704524-596810e891b5?w=600&q=80",
    graduation: "https://images.unsplash.com/photo-1591218214141-45545921d2d9?w=600&q=80",
    diverse: "https://images.unsplash.com/photo-1758270705290-62b6294dd044?w=600&q=80",
    campus: "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=600&q=80",
    international: "https://images.unsplash.com/photo-1608453162650-cba45689c284?w=600&q=80"
  };

  return (
    <div className="min-h-screen bg-white">
      {/* 1. Hero Section - "Your future starts here" */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/95 to-teal-600/90 z-10" />
        <img 
          src={studentImages.hero}
          alt="Students on campus"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Your future starts here
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
                Apply to college for the first time or transfer to complete your degree. 
                Navigate your entire college application journey with Wayo.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup">
                  <Button size="lg" className="w-full sm:w-auto text-lg px-8 py-6 bg-white text-emerald-700 hover:bg-gray-100 shadow-xl">
                    Start Your Application
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/how-it-works">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8 py-6 border-2 border-white text-white hover:bg-white/10">
                    Learn How It Works
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                <h3 className="text-center text-xl font-semibold mb-4 text-white/90">The goal for next 2 years</h3>
                <div className="grid grid-cols-2 gap-4 text-center text-white">
                  <div className="p-4">
                    <div className="text-4xl font-bold">900+</div>
                    <div className="text-white/80">Partner w/ Colleges</div>
                  </div>
                  <div className="p-4">
                    <div className="text-4xl font-bold">1M+</div>
                    <div className="text-white/80">Help Students</div>
                  </div>
                  <div className="p-4">
                    <div className="text-4xl font-bold">50+</div>
                    <div className="text-white/80">Expand to Countries</div>
                  </div>
                  <div className="p-4">
                    <div className="text-4xl font-bold">24/7</div>
                    <div className="text-white/80">AI Support</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats Mobile */}
      <section className="md:hidden bg-gradient-to-r from-emerald-600 to-teal-600 py-8">
        <div className="text-center text-white mb-4">
          <h3 className="text-lg font-semibold">The goal for next 2 years</h3>
        </div>
        <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto px-4 text-center text-white">
          <div><div className="text-3xl font-bold">900+</div><div className="text-sm">Partner w/ Colleges</div></div>
          <div><div className="text-3xl font-bold">1M+</div><div className="text-sm">Help Students</div></div>
          <div><div className="text-3xl font-bold">50+</div><div className="text-sm">Expand to Countries</div></div>
          <div><div className="text-3xl font-bold">24/7</div><div className="text-sm">AI Support</div></div>
        </div>
      </section>

      {/* 2. Explore Colleges in Our Database */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Explore colleges in our database</h2>
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-4xl mx-auto">
            <div className="flex flex-wrap gap-3 mb-6">
              <Link to="/colleges" className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium hover:bg-emerald-200 transition-colors cursor-pointer">First-year applications</Link>
              <Link to="/colleges" className="px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-medium hover:bg-teal-200 transition-colors cursor-pointer">Transfer applications</Link>
              <Link to="/colleges?type=public" className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium hover:bg-blue-200 transition-colors cursor-pointer">Public</Link>
              <Link to="/colleges?type=private" className="px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium hover:bg-purple-200 transition-colors cursor-pointer">Private</Link>
              <Link to="/colleges?size=small" className="px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium hover:bg-orange-200 transition-colors cursor-pointer">Small</Link>
              <Link to="/colleges?size=large" className="px-4 py-2 bg-pink-100 text-pink-700 rounded-full text-sm font-medium hover:bg-pink-200 transition-colors cursor-pointer">Large</Link>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/colleges" className="flex-1">
                <Button className="w-full text-lg py-6 bg-gradient-to-r from-emerald-500 to-teal-500">
                  <Search className="mr-2 h-5 w-5" />
                  Search Colleges
                </Button>
              </Link>
              <Link to="/admission-chances">
                <Button variant="outline" className="w-full sm:w-auto text-lg py-6 border-2">
                  <TrendingUp className="mr-2 h-5 w-5" />
                  Check Your Chances
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Meet Your AI Team */}
      <section className="py-20 bg-white" data-testid="ai-tools-showcase">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge className="bg-emerald-100 text-emerald-700 px-4 py-1.5 mb-4 text-sm font-medium">
              <Zap className="h-3.5 w-3.5 mr-1.5 inline" />
              Fully Operational
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Meet Your AI Team</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              5 AI-powered tools designed to give you an unfair advantage. Start using them today.
            </p>
          </div>

          {/* ALEC - Hero Feature Card */}
          <div className="mb-10">
            <Card className="overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-0 shadow-2xl" data-testid="alec-feature-card">
              <div className="grid md:grid-cols-2">
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <Badge className="w-fit bg-teal-500/20 text-teal-400 border-teal-500/30 mb-4">Most Popular</Badge>
                  <h3 className="text-3xl md:text-4xl font-bold text-white mb-3">ALEC — AI Visa Officer</h3>
                  <p className="text-slate-300 text-lg mb-6">
                    Practice any U.S. visa interview with a realistic AI consular officer. ALEC analyzes your DS-160 
                    and asks probing, visa-specific questions — just like the real thing.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {['F1 Student', 'H1B Work', 'B1/B2 Tourist', 'J1 Exchange', 'L1', 'O1', 'K1'].map(v => (
                      <span key={v} className="text-xs px-2.5 py-1 bg-white/10 text-slate-300 rounded-full">{v}</span>
                    ))}
                  </div>
                  <ul className="space-y-2.5 mb-8 text-slate-300">
                    <li className="flex items-center gap-2.5"><CheckCircle className="h-4 w-4 text-teal-400 flex-shrink-0" /> Deep DS-160 analysis with red flag detection</li>
                    <li className="flex items-center gap-2.5"><CheckCircle className="h-4 w-4 text-teal-400 flex-shrink-0" /> 4-phase interview with escalating difficulty</li>
                    <li className="flex items-center gap-2.5"><CheckCircle className="h-4 w-4 text-teal-400 flex-shrink-0" /> Section 214(b) non-immigrant intent scoring</li>
                    <li className="flex items-center gap-2.5"><CheckCircle className="h-4 w-4 text-teal-400 flex-shrink-0" /> Detailed feedback with sample better answers</li>
                  </ul>
                  <div className="flex items-center gap-4">
                    <Link to="/interview">
                      <Button size="lg" className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 shadow-lg" data-testid="try-alec-cta">
                        <Video className="h-5 w-5 mr-2" /> Try ALEC Now
                      </Button>
                    </Link>
                    <span className="text-slate-400 text-sm">25 credits / session</span>
                  </div>
                </div>
                <div className="hidden md:flex items-center justify-center p-8 bg-gradient-to-br from-teal-500/10 to-emerald-500/10">
                  <div className="bg-slate-700/50 backdrop-blur rounded-2xl p-6 border border-slate-600/50 w-full max-w-sm">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center">
                        <Mic className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">ALEC</p>
                        <p className="text-slate-400 text-xs">U.S. Consular Officer</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-slate-600/50 rounded-lg p-3">
                        <p className="text-slate-200 text-sm">"Good morning. Please hand me your passport. Now, tell me — why did you choose this particular university?"</p>
                      </div>
                      <div className="bg-teal-500/20 rounded-lg p-3 ml-8">
                        <p className="text-teal-200 text-sm">"I chose MIT because of their EECS program and the research work by Professor..."</p>
                      </div>
                      <div className="bg-slate-600/50 rounded-lg p-3">
                        <p className="text-slate-200 text-sm">"What happens after you graduate? Walk me through your plan."</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Other 4 AI Tools Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="ai-tools-grid">
            {/* WONG */}
            <Card className="group hover:shadow-xl transition-all hover:-translate-y-1 border-0 shadow-md" data-testid="wong-feature-card">
              <div className="p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Target className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">WONG</h3>
                <p className="text-blue-600 text-sm font-medium mb-3">Admission Predictor</p>
                <p className="text-gray-600 text-sm mb-4">
                  Get an AI-powered probability analysis of your admission chances at any university. Know where you stand before you apply.
                </p>
                <div className="flex items-center justify-between">
                  <Link to="/admission-chances" className="text-blue-600 font-medium text-sm flex items-center hover:underline" data-testid="try-wong-cta">
                    Try WONG <ChevronRight className="h-4 w-4 ml-1" />
                  </Link>
                  <span className="text-xs text-gray-400">2 credits</span>
                </div>
              </div>
            </Card>

            {/* Faith */}
            <Card className="group hover:shadow-xl transition-all hover:-translate-y-1 border-0 shadow-md" data-testid="faith-feature-card">
              <div className="p-6">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <BookOpen className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Faith</h3>
                <p className="text-purple-600 text-sm font-medium mb-3">Academic Advisor</p>
                <p className="text-gray-600 text-sm mb-4">
                  Not sure what to major in? Faith helps you discover the perfect major based on your interests, goals, and career aspirations.
                </p>
                <div className="flex items-center justify-between">
                  <Link to="/academic-advisor" className="text-purple-600 font-medium text-sm flex items-center hover:underline" data-testid="try-faith-cta">
                    Try Faith <ChevronRight className="h-4 w-4 ml-1" />
                  </Link>
                  <span className="text-xs text-gray-400">2 credits</span>
                </div>
              </div>
            </Card>

            {/* Ashish */}
            <Card className="group hover:shadow-xl transition-all hover:-translate-y-1 border-0 shadow-md" data-testid="ashish-feature-card">
              <div className="p-6">
                <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <TrendingUp className="h-6 w-6 text-pink-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Ashish</h3>
                <p className="text-pink-600 text-sm font-medium mb-3">Career Simulator</p>
                <p className="text-gray-600 text-sm mb-4">
                  Visualize your entire career path with AI. See salary projections, job growth trends, and the best route from degree to dream job.
                </p>
                <div className="flex items-center justify-between">
                  <Link to="/career-simulator" className="text-pink-600 font-medium text-sm flex items-center hover:underline" data-testid="try-ashish-cta">
                    Try Ashish <ChevronRight className="h-4 w-4 ml-1" />
                  </Link>
                  <span className="text-xs text-gray-400">2 credits</span>
                </div>
              </div>
            </Card>

            {/* Wasif */}
            <Card className="group hover:shadow-xl transition-all hover:-translate-y-1 border-0 shadow-md" data-testid="wasif-feature-card">
              <div className="p-6">
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <PenTool className="h-6 w-6 text-indigo-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Wasif</h3>
                <p className="text-indigo-600 text-sm font-medium mb-3">Essay Assistant</p>
                <p className="text-gray-600 text-sm mb-4">
                  Get detailed essay feedback and AI-detection scoring. Ensure your essay sounds authentically you before submitting.
                </p>
                <div className="flex items-center justify-between">
                  <Link to="/essay-review" className="text-indigo-600 font-medium text-sm flex items-center hover:underline" data-testid="try-wasif-cta">
                    Try Wasif <ChevronRight className="h-4 w-4 ml-1" />
                  </Link>
                  <span className="text-xs text-gray-400">3 credits</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* 4. University Hub */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="university-hub-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 px-4 py-1.5 mb-4 text-sm font-medium">
              <Building className="h-3.5 w-3.5 mr-1.5 inline" />
              For Colleges & Universities
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">University Hub</h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Partner with Wayo to reach qualified international students worldwide. Your own dashboard to manage applications, review candidates, and grow enrollment.
            </p>
          </div>

          {/* Hub Preview Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="bg-white/5 border-white/10 hover:border-emerald-500/50 transition-all hover:-translate-y-1" data-testid="hub-preview-dashboard">
              <div className="p-6">
                <div className="bg-emerald-500/20 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                  <Layout className="h-6 w-6 text-emerald-400" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2">Application Dashboard</h4>
                <p className="text-gray-400 text-sm mb-4">
                  View all incoming applications in real-time. Filter by GPA, test scores, nationality, and more. Make admission decisions directly from your dashboard.
                </p>
                <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                    <span>Applications</span><span>Reviewed</span><span>Decisions</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-emerald-400 font-bold">248</span>
                    <span className="text-amber-400 font-bold">186</span>
                    <span className="text-teal-400 font-bold">142</span>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="bg-white/5 border-white/10 hover:border-teal-500/50 transition-all hover:-translate-y-1" data-testid="hub-preview-analytics">
              <div className="p-6">
                <div className="bg-teal-500/20 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                  <BarChart3 className="h-6 w-6 text-teal-400" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2">Enrollment Analytics</h4>
                <p className="text-gray-400 text-sm mb-4">
                  Track your recruitment pipeline. See application trends by country, program, and semester. Data-driven insights to optimize your international enrollment.
                </p>
                <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                  <div className="flex items-end gap-1 h-12 justify-center">
                    {[40, 55, 35, 65, 50, 80, 70, 90].map((h, i) => (
                      <div key={i} className="w-4 bg-gradient-to-t from-teal-500 to-emerald-400 rounded-t" style={{height: `${h}%`}} />
                    ))}
                  </div>
                  <p className="text-center text-xs text-gray-500 mt-2">Applicants by month</p>
                </div>
              </div>
            </Card>

            <Card className="bg-white/5 border-white/10 hover:border-cyan-500/50 transition-all hover:-translate-y-1" data-testid="hub-preview-communication">
              <div className="p-6">
                <div className="bg-cyan-500/20 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                  <MessageSquare className="h-6 w-6 text-cyan-400" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2">Direct Communication</h4>
                <p className="text-gray-400 text-sm mb-4">
                  Message applicants directly through the platform. Send admission decisions, request documents, and answer questions — all in one place.
                </p>
                <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-cyan-500/30 rounded-full flex items-center justify-center"><span className="text-[10px] text-cyan-300">U</span></div>
                      <div className="bg-white/10 rounded px-2 py-1 text-xs text-gray-300 flex-1">Congratulations! You've been accepted...</div>
                    </div>
                    <div className="flex items-center gap-2 flex-row-reverse">
                      <div className="w-6 h-6 bg-emerald-500/30 rounded-full flex items-center justify-center"><span className="text-[10px] text-emerald-300">S</span></div>
                      <div className="bg-emerald-500/20 rounded px-2 py-1 text-xs text-emerald-300 flex-1">Thank you! I'm thrilled to accept!</div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Additional University Benefits */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="space-y-4">
              <div className="flex items-start gap-4 bg-white/5 rounded-xl p-5 border border-white/10">
                <div className="bg-emerald-500/20 p-3 rounded-lg flex-shrink-0">
                  <Shield className="h-6 w-6 text-emerald-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Document Verification</h4>
                  <p className="text-gray-400 text-sm">Transcripts, financial documents, and credentials verified through trusted sources. No more guessing.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 bg-white/5 rounded-xl p-5 border border-white/10">
                <div className="bg-teal-500/20 p-3 rounded-lg flex-shrink-0">
                  <Globe className="h-6 w-6 text-teal-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Global Student Reach</h4>
                  <p className="text-gray-400 text-sm">Connect with motivated students from 150+ countries seeking higher education at your institution.</p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-4 bg-white/5 rounded-xl p-5 border border-white/10">
                <div className="bg-cyan-500/20 p-3 rounded-lg flex-shrink-0">
                  <Database className="h-6 w-6 text-cyan-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Centralized Application Hub</h4>
                  <p className="text-gray-400 text-sm">Review applications, make decisions, and send offers from one unified platform built for international admissions.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 bg-white/5 rounded-xl p-5 border border-white/10">
                <div className="bg-amber-500/20 p-3 rounded-lg flex-shrink-0">
                  <Users className="h-6 w-6 text-amber-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Pre-Screened Applicants</h4>
                  <p className="text-gray-400 text-sm">Students build profiles and use AI tools before applying, so you receive higher-quality, well-prepared applications.</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <div className="inline-flex flex-col sm:flex-row gap-4">
              <Link to="/why-join">
                <Button size="lg" className="text-lg px-8 py-6 bg-gradient-to-r from-emerald-500 to-teal-500 shadow-lg" data-testid="become-partner-cta">
                  Become a Partner University
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/university/login">
                <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-white text-white hover:bg-white/10" data-testid="university-login-cta">
                  University Hub Login
                </Button>
              </Link>
            </div>
            <p className="text-gray-500 text-sm mt-4">Free to join during our launch phase. Limited partner spots available.</p>
          </div>
        </div>
      </section>

      {/* 5. Why Pay More? */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white" data-testid="pricing-comparison-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge className="bg-amber-100 text-amber-700 px-4 py-1.5 mb-4 text-sm font-medium">
              <DollarSign className="h-3.5 w-3.5 mr-1.5 inline" />
              Save 90%+ vs. Traditional Services
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Why Pay More?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Get the same quality guidance at a fraction of the cost. Our AI tools deliver expert-level results instantly.
            </p>
          </div>

          {/* Comparison Table */}
          <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-xl mb-12" data-testid="pricing-table">
            {/* Table Header */}
            <div className="grid grid-cols-12 bg-slate-900 text-white">
              <div className="col-span-4 p-5 font-semibold text-lg">Service</div>
              <div className="col-span-4 p-5 text-center">
                <span className="text-red-400 font-semibold text-lg">Traditional</span>
                <p className="text-xs text-gray-400 mt-0.5">Person / Company</p>
              </div>
              <div className="col-span-4 p-5 text-center bg-emerald-600/20 border-l border-emerald-500/30">
                <span className="text-emerald-400 font-semibold text-lg">Wayo AI</span>
                <p className="text-xs text-emerald-300/70 mt-0.5">Instant & Unlimited Retries</p>
              </div>
            </div>

            {/* ALEC Row */}
            <div className="grid grid-cols-12 border-t border-gray-100 hover:bg-gray-50/50 transition-colors" data-testid="compare-alec">
              <div className="col-span-4 p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Video className="h-5 w-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">ALEC</p>
                    <p className="text-xs text-gray-500">Visa Interview Practice</p>
                  </div>
                </div>
              </div>
              <div className="col-span-4 p-5 text-center flex flex-col justify-center">
                <p className="text-2xl font-bold text-gray-900">$150–$350</p>
                <p className="text-xs text-gray-500 mt-1">Immigration lawyer / mock session</p>
                <p className="text-xs text-gray-400">1 session, 1 visa type, limited feedback</p>
              </div>
              <div className="col-span-4 p-5 text-center bg-emerald-50/50 border-l border-emerald-100 flex flex-col justify-center">
                <p className="text-2xl font-bold text-emerald-600">~$20</p>
                <p className="text-xs text-gray-500 mt-1">25 credits per session</p>
                <p className="text-xs text-emerald-600 font-medium">All visa types, deep analysis, retry anytime</p>
              </div>
            </div>

            {/* WONG Row */}
            <div className="grid grid-cols-12 border-t border-gray-100 hover:bg-gray-50/50 transition-colors" data-testid="compare-wong">
              <div className="col-span-4 p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Target className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">WONG</p>
                    <p className="text-xs text-gray-500">Admission Predictor</p>
                  </div>
                </div>
              </div>
              <div className="col-span-4 p-5 text-center flex flex-col justify-center">
                <p className="text-2xl font-bold text-gray-900">$200–$500</p>
                <p className="text-xs text-gray-500 mt-1">College counselor consultation</p>
                <p className="text-xs text-gray-400">1 hour, subjective opinion</p>
              </div>
              <div className="col-span-4 p-5 text-center bg-emerald-50/50 border-l border-emerald-100 flex flex-col justify-center">
                <p className="text-2xl font-bold text-emerald-600">~$1.50</p>
                <p className="text-xs text-gray-500 mt-1">2 credits per analysis</p>
                <p className="text-xs text-emerald-600 font-medium">Data-driven, instant, check multiple schools</p>
              </div>
            </div>

            {/* Wasif Row */}
            <div className="grid grid-cols-12 border-t border-gray-100 hover:bg-gray-50/50 transition-colors" data-testid="compare-wasif">
              <div className="col-span-4 p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <PenTool className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Wasif</p>
                    <p className="text-xs text-gray-500">Essay Review + AI Detection</p>
                  </div>
                </div>
              </div>
              <div className="col-span-4 p-5 text-center flex flex-col justify-center">
                <p className="text-2xl font-bold text-gray-900">$100–$500</p>
                <p className="text-xs text-gray-500 mt-1">Essay editing service (EssayEdge, etc.)</p>
                <p className="text-xs text-gray-400">3-5 day turnaround, no AI detection</p>
              </div>
              <div className="col-span-4 p-5 text-center bg-emerald-50/50 border-l border-emerald-100 flex flex-col justify-center">
                <p className="text-2xl font-bold text-emerald-600">~$2.50</p>
                <p className="text-xs text-gray-500 mt-1">3 credits per review</p>
                <p className="text-xs text-emerald-600 font-medium">Instant feedback + AI detection scoring</p>
              </div>
            </div>

            {/* Faith Row */}
            <div className="grid grid-cols-12 border-t border-gray-100 hover:bg-gray-50/50 transition-colors" data-testid="compare-faith">
              <div className="col-span-4 p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BookOpen className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Faith</p>
                    <p className="text-xs text-gray-500">Academic Advisor</p>
                  </div>
                </div>
              </div>
              <div className="col-span-4 p-5 text-center flex flex-col justify-center">
                <p className="text-2xl font-bold text-gray-900">$150–$300</p>
                <p className="text-xs text-gray-500 mt-1">Academic advisor session</p>
                <p className="text-xs text-gray-400">Availability varies, single perspective</p>
              </div>
              <div className="col-span-4 p-5 text-center bg-emerald-50/50 border-l border-emerald-100 flex flex-col justify-center">
                <p className="text-2xl font-bold text-emerald-600">~$1.50</p>
                <p className="text-xs text-gray-500 mt-1">2 credits per session</p>
                <p className="text-xs text-emerald-600 font-medium">Personalized, instant, explore unlimited majors</p>
              </div>
            </div>

            {/* Ashish Row */}
            <div className="grid grid-cols-12 border-t border-gray-100 hover:bg-gray-50/50 transition-colors" data-testid="compare-ashish">
              <div className="col-span-4 p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-5 w-5 text-pink-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Ashish</p>
                    <p className="text-xs text-gray-500">Career Simulator</p>
                  </div>
                </div>
              </div>
              <div className="col-span-4 p-5 text-center flex flex-col justify-center">
                <p className="text-2xl font-bold text-gray-900">$200–$400</p>
                <p className="text-xs text-gray-500 mt-1">Career coach session</p>
                <p className="text-xs text-gray-400">General guidance, no data-driven projections</p>
              </div>
              <div className="col-span-4 p-5 text-center bg-emerald-50/50 border-l border-emerald-100 flex flex-col justify-center">
                <p className="text-2xl font-bold text-emerald-600">~$1.50</p>
                <p className="text-xs text-gray-500 mt-1">2 credits per simulation</p>
                <p className="text-xs text-emerald-600 font-medium">Salary projections, job trends, visual path</p>
              </div>
            </div>

            {/* Total Savings Row */}
            <div className="grid grid-cols-12 border-t-2 border-gray-200 bg-gradient-to-r from-slate-50 to-emerald-50">
              <div className="col-span-4 p-5">
                <p className="font-bold text-gray-900 text-lg">Total (all 5 tools)</p>
                <p className="text-xs text-gray-500">One session of each</p>
              </div>
              <div className="col-span-4 p-5 text-center flex flex-col justify-center">
                <p className="text-3xl font-bold text-red-500 line-through decoration-2">$800–$2,050</p>
              </div>
              <div className="col-span-4 p-5 text-center bg-emerald-50 border-l border-emerald-200 flex flex-col justify-center">
                <p className="text-3xl font-bold text-emerald-600">~$13</p>
                <p className="text-sm text-emerald-700 font-semibold mt-1">Save over 98%</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Link to="/pricing">
              <Button size="lg" className="text-lg px-10 py-6 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-lg" data-testid="view-pricing-cta">
                View Credit Packages
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <p className="text-gray-500 text-sm mt-3">No subscriptions. Credits never expire. Start with just $10.</p>
          </div>
        </div>
      </section>

      {/* 6. Plan Your Future */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Plan your future</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get connected with everything you need to apply to college, research financial aid and scholarships, 
              and get advice from counselors, advisors and mentors.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Why College Matters */}
            <Card className="overflow-hidden hover:shadow-xl transition-all group">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={studentImages.graduation}
                  alt="Graduation celebration"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <GraduationCap className="h-8 w-8 text-white" />
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">Why college matters</h3>
                <p className="text-gray-600 mb-4">College is worth it. Your future is worth it. You are worth it.</p>
                <Link to="/why-college-matters" className="text-emerald-600 font-medium flex items-center hover:underline">
                  Learn why college matters
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </Card>

            {/* Paying for College */}
            <Card className="overflow-hidden hover:shadow-xl transition-all group">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={studentImages.studying}
                  alt="Students studying together"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <Award className="h-8 w-8 text-white" />
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">Paying for college</h3>
                <p className="text-gray-600 mb-4">There are lots of options available to you. We can help you find them.</p>
                <Link to="/paying-for-college" className="text-emerald-600 font-medium flex items-center hover:underline">
                  Learn how to pay for college
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </Card>

            {/* Your College Roadmap */}
            <Card className="overflow-hidden hover:shadow-xl transition-all group">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={studentImages.campus}
                  alt="College campus"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <Compass className="h-8 w-8 text-white" />
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">Your college roadmap</h3>
                <p className="text-gray-600 mb-4">The path may seem unclear. We can help you find your way.</p>
                <Link to="/how-it-works" className="text-emerald-600 font-medium flex items-center hover:underline">
                  Learn how to get to college
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* 7. Built for Students Like You */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src={studentImages.international}
                alt="International student"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-4 hidden md:block">
                <div className="flex items-center gap-3">
                  <Globe className="h-10 w-10 text-emerald-600" />
                  <div>
                    <div className="text-2xl font-bold text-gray-900">150+</div>
                    <div className="text-gray-600">Countries</div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="inline-block mb-4 px-4 py-2 bg-emerald-100 rounded-full text-emerald-700 text-sm font-semibold">
                FOR INTERNATIONAL STUDENTS
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Built for students like you</h2>
              <p className="text-xl text-gray-600 mb-8">
                We understand the unique challenges international students face. From visa preparation to 
                document verification, we're here to guide you every step of the way.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <FileText className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold">F-1 Visa Guide</h4>
                    <p className="text-gray-600 text-sm">Step-by-step guidance through the visa application process</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-teal-100 p-2 rounded-lg">
                    <Video className="h-5 w-5 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold">ALEC - AI Interview Practice</h4>
                    <p className="text-gray-600 text-sm">Practice your visa interview with ALEC, our AI powered simulator</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-cyan-100 p-2 rounded-lg">
                    <Shield className="h-5 w-5 text-cyan-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Document Verification</h4>
                    <p className="text-gray-600 text-sm">Get your documents verified for trusted applications</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/f1-guide">
                  <Button size="lg" className="bg-gradient-to-r from-emerald-500 to-teal-500">
                    F-1 Visa Guide
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/interview">
                  <Button size="lg" variant="outline">
                    Practice Interview
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. Faith - AI Academic Advisor */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-60 h-60 bg-white rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-white/20 rounded-full text-sm font-semibold">
                <Sparkles className="h-4 w-4" />
                AI-POWERED - 2 CREDITS
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Faith - AI Academic Advisor</h2>
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                Not sure what to major in? Faith, our AI powered academic advisor, helps you discover 
                the perfect major based on your interests, career goals, and where you want to work.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <Target className="h-4 w-4" />
                  </div>
                  <span>Personalized major recommendations</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <Globe className="h-4 w-4" />
                  </div>
                  <span>Career insights for your target country</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <GraduationCap className="h-4 w-4" />
                  </div>
                  <span>College recommendations when you're ready</span>
                </div>
              </div>
              <Link to="/academic-advisor">
                <Button size="lg" className="bg-white text-indigo-700 hover:bg-gray-100 shadow-xl px-8">
                  Try Faith - Academic Advisor
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <p className="text-sm text-white/70 mt-4">2 credits per session</p>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-indigo-400 to-purple-400 rounded-full flex items-center justify-center">
                    <GraduationCap className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 bg-white/10 rounded-lg p-3">
                    <p className="text-sm">Based on your interest in technology and goal to work in the US, I recommend exploring Computer Science, Data Science, or Software Engineering...</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 flex-row-reverse">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Users className="h-5 w-5" />
                  </div>
                  <div className="flex-1 bg-indigo-500/50 rounded-lg p-3">
                    <p className="text-sm">What's the job market like for Data Science in the US?</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 9. Student Success Stories */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Student Success Stories</h2>
            <p className="text-xl text-gray-600">See how our AI tools are helping students prepare smarter</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6 bg-white">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />)}
              </div>
              <p className="text-gray-600 mb-6 italic" data-testid="testimonial-1">
                "ALEC helped me practice my H-1B visa interview over and over until I felt confident. The questions were
                exactly what the real officer asked. I got approved on my first try!"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <span className="text-emerald-600 font-bold">RK</span>
                </div>
                <div>
                  <div className="font-semibold">Raj K.</div>
                  <div className="text-sm text-gray-500">H-1B Approved, Software Engineer</div>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-white">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />)}
              </div>
              <p className="text-gray-600 mb-6 italic" data-testid="testimonial-2">
                "Wasif caught AI-written sections in my essay that I didn't even realize were there. The feedback was
                so detailed — it helped me rewrite everything in my own voice before submitting."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                  <span className="text-teal-600 font-bold">SA</span>
                </div>
                <div>
                  <div className="font-semibold">Sarah A.</div>
                  <div className="text-sm text-gray-500">Used Wasif for Essay Review</div>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-white">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />)}
              </div>
              <p className="text-gray-600 mb-6 italic" data-testid="testimonial-3">
                "WONG's Chance Me tool gave me a realistic picture of where I stood. Faith then helped me pick the right
                major based on my strengths. These AI tools are a game changer."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
                  <span className="text-cyan-600 font-bold">ML</span>
                </div>
                <div>
                  <div className="font-semibold">Maria L.</div>
                  <div className="text-sm text-gray-500">Used WONG & Faith for Planning</div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to start your journey?</h2>
          <p className="text-xl text-white/90 mb-8">
            Join over 1 million students who have found their way forward with Wayo.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button size="lg" className="text-lg px-10 py-7 bg-white text-emerald-700 hover:bg-gray-100 shadow-xl">
                Create Your Account
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/faq">
              <Button size="lg" variant="outline" className="text-lg px-10 py-7 border-2 border-white text-white hover:bg-white/10">
                Have Questions?
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-gray-300 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-5 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-2 rounded-lg">
                  <Compass className="h-6 w-6 text-white" />
                </div>
                <span className="text-2xl font-bold text-white">Wayo</span>
              </div>
              <p className="text-gray-400 mb-6">
                Your way forward. Helping international students navigate their path to higher education since 2019.
              </p>
              <div className="flex gap-4">
                <a href="#" className="hover:text-white transition-colors"><Instagram className="h-5 w-5" /></a>
                <a href="#" className="hover:text-white transition-colors"><Twitter className="h-5 w-5" /></a>
                <a href="#" className="hover:text-white transition-colors"><Facebook className="h-5 w-5" /></a>
                <a href="#" className="hover:text-white transition-colors"><Linkedin className="h-5 w-5" /></a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">For Students</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/signup" className="hover:text-white">Create Account</Link></li>
                <li><Link to="/colleges" className="hover:text-white">Search Colleges</Link></li>
                <li><Link to="/f1-guide" className="hover:text-white">F-1 Visa Guide</Link></li>
                <li><Link to="/admission-chances" className="hover:text-white">WONG - Chance Me</Link></li>
                <li><Link to="/interview" className="hover:text-white">ALEC - AI Visa Officer</Link></li>
                <li><Link to="/essay-review" className="hover:text-white">Wasif - Essay Assistant</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">For Members</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/why-join" className="hover:text-white">Why Join Wayo</Link></li>
                <li><Link to="/university/login" className="hover:text-white">University Hub</Link></li>
                <li><Link to="/counselor" className="hover:text-white">Counselor Portal</Link></li>
                <li><Link to="/parent" className="hover:text-white">Parent Portal</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white">About Us</Link></li>
                <li><Link to="/about/founders" className="hover:text-white">Our Founder</Link></li>
                <li><Link to="/careers" className="hover:text-white">Careers</Link></li>
                <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-sm text-gray-500">
            <p>&copy; 2026 Wayo. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
